import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Lightbulb, Target, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function BestPractices() {
  return (
    <div className="guide-container">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>Best Practices Playbook</h1>
        <p className="guide-subtitle">Industry standards that help you stay competitive and keep customers loyal</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>What Sets Winners Apart</h2>
          <p>
            Best practices aren't exotic strategies. They're fundamental principles that every successful business follows. 
            Master these and you'll outcompete businesses that don't.
          </p>
        </section>

        <section className="guide-section">
          <h2>Core Best Practices</h2>
          
          <div className="practices-section">
            <h3><CheckCircle size={24} /> 1. Mobile-First Thinking</h3>
            <p>
              60% of searches happen on mobile. If your website looks bad on a phone, you're already losing. 
              Every experience should work perfectly on mobile first.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Test your website on a smartphone right now</li>
                <li>Make sure buttons are large enough to tap</li>
                <li>Minimize the number of steps to purchase</li>
                <li>Pages should load in under 3 seconds</li>
              </ul>
            </div>
          </div>

          <div className="practices-section">
            <h3><CheckCircle size={24} /> 2. Transparency & Trust</h3>
            <p>
              Customers buy from businesses they trust. Make it easy for them to trust you by being transparent 
              about pricing, policies, and what to expect.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Show all-inclusive pricing (no hidden fees)</li>
                <li>Display a clear return/refund policy</li>
                <li>Show real customer photos, not just stock photos</li>
                <li>Be honest about delivery times</li>
                <li>Display customer reviews prominently</li>
              </ul>
            </div>
          </div>

          <div className="practices-section">
            <h3><CheckCircle size={24} /> 3. Frictionless Checkout</h3>
            <p>
              40% of online purchases are abandoned at checkout. Every extra step loses customers. 
              Make buying as easy as physically possible.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Minimize form fields (only essential info)</li>
                <li>Offer multiple payment methods</li>
                <li>Show progress (step 1 of 3, etc.)</li>
                <li>Allow guest checkout (don't force account creation)</li>
                <li>Display security badges</li>
              </ul>
            </div>
          </div>

          <div className="practices-section">
            <h3><CheckCircle size={24} /> 4. Email Best Practices</h3>
            <p>
              Email is the most effective marketing channel. Done right, it builds relationships and drives sales.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Send welcome email immediately after signup</li>
                <li>Keep email list segmented (different messages for different groups)</li>
                <li>Personalize emails with customer name/history</li>
                <li>Send promotions to inactive customers</li>
                <li>Include clear unsubscribe (it's the law)</li>
                <li>Test subject lines to improve open rates</li>
              </ul>
            </div>
          </div>

          <div className="practices-section">
            <h3><CheckCircle size={24} /> 5. Customer Service Excellence</h3>
            <p>
              Customer service is your competitive advantage. Every interaction is an opportunity to wow or lose a customer.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Respond to customer inquiries within 24 hours</li>
                <li>Have multiple contact methods (phone, email, chat, text)</li>
                <li>Empower your team to solve problems without escalation</li>
                <li>Follow up after purchase to ensure satisfaction</li>
                <li>Treat complaints as opportunities to delight</li>
              </ul>
            </div>
          </div>

          <div className="practices-section">
            <h3><CheckCircle size={24} /> 6. Data Privacy & Security</h3>
            <p>
              Customers trust you with their data. Protecting it isn't just ethical—it's essential for your reputation.
            </p>
            <div className="best-practice-detail">
              <h4>How to implement:</h4>
              <ul>
                <li>Use SSL certificates (look for the padlock)</li>
                <li>Never store unnecessary customer data</li>
                <li>Have a clear privacy policy</li>
                <li>Don't sell customer data to third parties</li>
                <li>Follow payment security standards (PCI DSS)</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Content Best Practices</h2>
          <div className="content-practices">
            <div className="content-card">
              <h3>📝 Product Descriptions</h3>
              <p>Don't just list features. Explain benefits. "Waterproof" is a feature. "Use it in any weather" is a benefit.</p>
            </div>

            <div className="content-card">
              <h3>📸 Product Photos</h3>
              <p>Use real photos from multiple angles. Include size references. Show the product in use. 
              Products with multiple photos have 27% higher conversion rates.</p>
            </div>

            <div className="content-card">
              <h3>⭐ Customer Reviews</h3>
              <p>Display real reviews (good and bad). Respond to all reviews. Fake reviews destroy credibility.</p>
            </div>

            <div className="content-card">
              <h3>📖 Clear Communications</h3>
              <p>Use simple language. Avoid jargon. Mobile users scan, they don't read. Use bullet points and short paragraphs.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Common Pitfalls to Avoid</h2>
          <div className="pitfalls">
            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Ignoring Mobile</h3>
              <p>If your site doesn't work on phones, you're invisible to most customers.</p>
            </div>

            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Hidden Fees</h3>
              <p>Show your true price upfront. Surprise fees destroy trust and kill sales.</p>
            </div>

            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Ignoring Customer Feedback</h3>
              <p>Your customers are telling you what to fix. Listen to them.</p>
            </div>

            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Inconsistent Branding</h3>
              <p>Look different on every platform and customers won't trust you. Be consistent.</p>
            </div>

            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Slow Shipping/Delivery</h3>
              <p>Set expectations and exceed them. Slow fulfillment = negative reviews.</p>
            </div>

            <div className="pitfall-card">
              <AlertCircle size={24} />
              <h3>❌ Poor Customer Service</h3>
              <p>One bad experience gets shared with 10 friends. One great experience gets shared with 1 friend. 
              Negative spreads faster.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>A/B Testing Framework</h2>
          <p>The best businesses test continuously. Small improvements add up.</p>
          <div className="testing-framework">
            <div className="test-card">
              <Target size={24} />
              <h3>What to Test</h3>
              <ul>
                <li>Call-to-action button text & color</li>
                <li>Product descriptions</li>
                <li>Pricing & discount amounts</li>
                <li>Email subject lines</li>
                <li>Homepage headlines</li>
              </ul>
            </div>

            <div className="test-card">
              <Lightbulb size={24} />
              <h3>How to Test</h3>
              <ul>
                <li>Change ONE thing at a time</li>
                <li>Run for at least 1-2 weeks</li>
                <li>Get statistical significance (100+ conversions)</li>
                <li>Keep winners, discard losers</li>
                <li>Test the next variable</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>The Best Practice Mindset</h2>
          <p>
            Best practices aren't about perfection. They're about consistently putting your customer first. 
            Ask yourself: "Would I want this experience?"
          </p>
          <p>
            If the answer is no, change it. If you're unsure, test it. Your competition might be ignoring these 
            fundamentals. That's your advantage.
          </p>
        </section>

        <section className="guide-cta">
          <h2>Ready to Implement?</h2>
          <p>Our platform is built on these best practices. See how they're implemented.</p>
          <Link to="/features" className="guide-cta-btn">View Features <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources" className="back-to-resources">
          ← Back to All Resources
        </Link>
      </div>
    </div>
  );
}

export default BestPractices;
